export const ARETI_WIDGET_DOM_ID = 'areti-widget';
export const ARETI_WIDGET_MAX_WIDTH_PX = 430;

const DEFAULT_ACCOUNT_SID = 'AC972aa86e50a146881a05a5ba0d04d80d';
const DEFAULT_CDN = 'https://cdn-webchat-staging.aretihealth.com';
const DEFAULT_SERVER_URL = 'https://api-shared.aretihealth.com/iqvia';
const DEFAULT_SURVEY_URL = 'https://survey.aretihealth.com';

const INIT_POLL_MS = 80;
const INIT_POLL_TRIES = 125;
const ON_LOADED_FAILSAFE_MS = 25_000;

export interface AretiWidgetConfig {
  accountSid: string;
  cdn: string;
  serverUrl: string;
  actionType: string;
  surveyUrl: string;
  withZipCode: boolean;
  header: string;
  footer: string;
  studyName: string;
  color: string;
  options: {
    study: string;
    deployment_id: string;
    source_type: string;
    site: string;
    customer_payload: {
      guestRef?: string;
      enrollmentId?: string;
    };
  };
}

const defaultAretiConfig: AretiWidgetConfig = {
  accountSid: process.env.NEXT_PUBLIC_ARETI_ACCOUNT_SID?.trim() || DEFAULT_ACCOUNT_SID,
  cdn: process.env.NEXT_PUBLIC_ARETI_CDN?.trim() || DEFAULT_CDN,
  serverUrl: process.env.NEXT_PUBLIC_ARETI_SERVER_URL?.trim() || DEFAULT_SERVER_URL,
  actionType: 'initial call',
  surveyUrl: process.env.NEXT_PUBLIC_ARETI_SURVEY_URL?.trim() || DEFAULT_SURVEY_URL,
  withZipCode: false,
  header: 'IQVIA AI Coordinator',
  footer: `By submitting this form, you consent to receive informational messages from IQVIA powered by Areti Health about current and future studies. Message and data rates may apply. Message frequency varies. Reply HELP for help, STOP to opt-out. Please refer to <a href="https://www.iqvia.com/about-us/privacy/privacy-policy" rel="noopener noreferrer">IQVIA privacy policy</a>, <a href="https://www.aretihealth.com/privacy-policy/" rel="noopener noreferrer">Areti Health privacy policy</a> and <a href="https://www.aretihealth.com/terms-of-service/" rel="noopener noreferrer">Areti Health terms of service</a>.`,
  studyName: 'Heart Failure Study',
  color: '#0066CC',
  options: {
    study: 'easihf_study',
    deployment_id: 'no_site_easihf_study',
    source_type: 'web',
    site: 'no_site',
    customer_payload: {
      guestRef: '',
      enrollmentId: '',
    },
  },
};

function widgetScriptUrl(cdnBase: string): string {
  const base = cdnBase.replace(/\/+$/, '');
  return `${base}/areti-widget.js`;
}

function mergeConfig(override?: Partial<AretiWidgetConfig> | null): AretiWidgetConfig {
  if (!override || (typeof override === 'object' && Object.keys(override).length === 0)) {
    return defaultAretiConfig;
  }
  return {
    ...defaultAretiConfig,
    ...override,
    options: {
      ...defaultAretiConfig.options,
      ...(override.options ?? {}),
      customer_payload: {
        ...defaultAretiConfig.options.customer_payload,
        ...(override.options?.customer_payload ?? {}),
      },
    },
  };
}

declare global {
  interface Window {
    Areti?: {
      initWebchat: (config: AretiWidgetConfig) => void;
      open?: () => void;
    };
  }
}

function loadScript(scriptSrc: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document not available'));
      return;
    }
    if (document.querySelector(`script[src="${scriptSrc}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = scriptSrc;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Areti widget script'));
    document.head.appendChild(s);
  });
}

function tryInit(config: AretiWidgetConfig): boolean {
  if (!window.Areti?.initWebchat) return false;
  const guestRef = config.options?.customer_payload?.guestRef?.trim();
  const enrollmentId = config.options?.customer_payload?.enrollmentId?.trim();
  if (!guestRef || !enrollmentId) return false;
  try {
    window.Areti.initWebchat(config);
    window.Areti.open?.();
  } catch (e) {
    console.error('[Areti chatbot] initWebchat error', e);
  }
  return true;
}

async function loadAndInit(config: AretiWidgetConfig): Promise<void> {
  if (tryInit(config)) return;
  await loadScript(widgetScriptUrl(config.cdn));
  for (let i = 0; i < INIT_POLL_TRIES; i += 1) {
    if (tryInit(config)) return;
    await new Promise<void>((r) => window.setTimeout(r, INIT_POLL_MS));
  }
  console.warn('[Areti chatbot] initWebchat not available after script load');
}

/**
 * @param configOrOnLoaded — Sitecore partial config, or a lone callback.
 * @param onLoaded — Fires when init was attempted (or failsafe timeout).
 */
export function openAretiChatbot(
  configOrOnLoaded?: Partial<AretiWidgetConfig> | null | (() => void),
  onLoaded?: () => void
): void {
  if (typeof window === 'undefined') return;

  const override =
    typeof configOrOnLoaded === 'function' ? undefined : configOrOnLoaded ?? undefined;
  const done = typeof configOrOnLoaded === 'function' ? configOrOnLoaded : onLoaded;

  void (async () => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      done?.();
    };
    const failsafe = window.setTimeout(finish, ON_LOADED_FAILSAFE_MS);
    try {
      if (!document.getElementById(ARETI_WIDGET_DOM_ID)) {
        console.warn(
          `[Areti chatbot] #${ARETI_WIDGET_DOM_ID} not in DOM — call openAretiChatbot only after AretiHealthScreener mounts.`
        );
      }
      await loadAndInit(mergeConfig(override));
    } catch (err) {
      console.error('[Areti chatbot]', err);
    } finally {
      window.clearTimeout(failsafe);
      finish();
    }
  })();
}
